import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import Map, { Marker } from 'react-map-gl';
import type { MapRef, MapLayerMouseEvent } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const mapboxAccessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
const defaultMapStyle = 'mapbox://styles/mapbox/streets-v12';

export type MapViewHandle = {
	flyTo: (lng: number, lat: number, zoom?: number) => void;
	zoomIn: () => void;
	zoomOut: () => void;
};

type Props = {
	latitude: number;
	longitude: number;
	onSelect: (lng: number, lat: number) => void;
};

const MapView = forwardRef<MapViewHandle, Props>(function MapView(
	{ latitude, longitude, onSelect },
	ref,
) {
	const mapRef = useRef<MapRef | null>(null);
	const skipNextRecenterRef = useRef(false);

	useEffect(() => {
		if (skipNextRecenterRef.current) {
			skipNextRecenterRef.current = false;
			return;
		}
		const map = mapRef.current?.getMap();
		if (!map) return;
		const currentCenter = map.getCenter();
		if (
			Math.abs(currentCenter.lng - longitude) > 1e-6 ||
			Math.abs(currentCenter.lat - latitude) > 1e-6
		) {
			map.jumpTo({ center: [longitude, latitude] });
		}
	}, [latitude, longitude]);

	useImperativeHandle(ref, () => ({
		flyTo: (lng, lat, zoom) => {
			const map = mapRef.current?.getMap();
			if (!map) return;
			map.flyTo({ center: [lng, lat], zoom: zoom ?? map.getZoom(), essential: true });
		},
		zoomIn: () => mapRef.current?.getMap().zoomIn(),
		zoomOut: () => mapRef.current?.getMap().zoomOut(),
	}));

	const handleClick = (event: MapLayerMouseEvent) => {
		skipNextRecenterRef.current = true;
		onSelect(event.lngLat.lng, event.lngLat.lat);
	};

	return (
		<Map
			ref={mapRef}
			mapboxAccessToken={mapboxAccessToken}
			initialViewState={{ longitude, latitude, zoom: 15 }}
			mapStyle={defaultMapStyle}
			onClick={handleClick}
			style={{ width: '100%', height: '100%' }}
			attributionControl={false}
		>
			<Marker longitude={longitude} latitude={latitude} anchor="bottom">
				<svg
					width="28"
					height="38"
					viewBox="0 0 24 32"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden="true"
					style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.35))' }}
				>
					<path
						d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z"
						fill="#E23B3B"
					/>
					<circle cx="12" cy="12" r="4.5" fill="#FFFFFF" />
				</svg>
			</Marker>
		</Map>
	);
});

export default MapView;
